package com.havn.app.features.auth.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.ui.theme.LocalHavnThemeTokens
import havn.composeapp.generated.resources.Res
import havn.composeapp.generated.resources.havn_auth_logo
import org.jetbrains.compose.resources.painterResource

private const val AppStoreUrl = "https://apps.apple.com/search?term=HAVN%20Real%20Estate"
private const val GooglePlayUrl = "https://play.google.com/store/apps/details?id=binarts.havnre.app"

@Composable
fun AuthScreen(
    uiState: AuthUiState,
    onEmailChanged: (String) -> Unit,
    onOtpCodeChanged: (String) -> Unit,
    onSendEmailOtp: () -> Unit,
    onVerifyEmailOtp: () -> Unit,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowWelcome: () -> Unit,
    onShowEmailEntry: () -> Unit
) {
    when (uiState.step) {
        AuthStep.Welcome -> WelcomeScreen(
            uiState = uiState,
            onSignInWithGoogle = onSignInWithGoogle,
            onSignInWithApple = onSignInWithApple,
            onShowEmailEntry = onShowEmailEntry
        )
        AuthStep.EmailEntry -> EmailEntryScreen(
            uiState = uiState,
            onEmailChanged = onEmailChanged,
            onSendEmailOtp = onSendEmailOtp,
            onBack = onShowWelcome
        )
        AuthStep.VerifyCode -> VerifyCodeScreen(
            uiState = uiState,
            onOtpCodeChanged = onOtpCodeChanged,
            onVerifyEmailOtp = onVerifyEmailOtp,
            onResend = onSendEmailOtp,
            onBack = onShowEmailEntry
        )
    }
}

@Composable
private fun WelcomeScreen(
    uiState: AuthUiState,
    onSignInWithGoogle: () -> Unit,
    onSignInWithApple: () -> Unit,
    onShowEmailEntry: () -> Unit
) {
    AuthFormFrame(
        subtitle = "Open houses, contacts, and follow-ups in one secure agent workspace.",
        showLogo = true,
        centerSubtitle = true,
        compact = true
    ) {
        if (HavnAuthPlatform.supportsAppleSignIn) {
            HavnButton(
                text = "Continue with Apple",
                onClick = onSignInWithApple,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Apple,
                modifier = Modifier.fillMaxWidth()
            )
        }
        HavnButton(
            text = "Continue with Google",
            onClick = onSignInWithGoogle,
            enabled = !uiState.isSubmitting,
            style = HavnButtonStyle.Secondary,
            leadingIcon = HavnIconKey.Google,
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = "Continue with Email",
            onClick = onShowEmailEntry,
            enabled = !uiState.isSubmitting,
            leadingIcon = HavnIconKey.Mail,
            modifier = Modifier.fillMaxWidth()
        )
        if (HavnAuthPlatform.supportsAppleSignIn) {
            HelperText("When Apple asks, choose Share My Email so HAVN receives your real email address.")
        }
        AuthMessages(uiState)
        WebStoreBadges()
    }
}

@Composable
private fun EmailEntryScreen(
    uiState: AuthUiState,
    onEmailChanged: (String) -> Unit,
    onSendEmailOtp: () -> Unit,
    onBack: () -> Unit
) {
    AuthFormFrame(
        subtitle = "Enter your email and we will send a sign-in code plus a magic link.",
        compact = true
    ) {
        HavnTextField(
            value = uiState.email,
            onValueChange = onEmailChanged,
            label = "Email",
            isError = uiState.emailError != null,
            errorText = uiState.emailError,
            leadingIcon = HavnIconKey.Mail,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = "Send code",
            onClick = onSendEmailOtp,
            enabled = !uiState.isSubmitting,
            loading = uiState.isSubmitting,
            leadingIcon = HavnIconKey.Mail,
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = "Back",
            onClick = onBack,
            enabled = !uiState.isSubmitting,
            style = HavnButtonStyle.Ghost,
            leadingIcon = HavnIconKey.Back,
            modifier = Modifier.fillMaxWidth()
        )
        AuthMessages(uiState)
        WebStoreBadges()
    }
}

@Composable
private fun WebStoreBadges() {
    if (!havnIsWebShell) return
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val stack = maxWidth < 360.dp
        val arrangement = Arrangement.spacedBy(10.dp)
        if (stack) {
            Column(verticalArrangement = arrangement, modifier = Modifier.fillMaxWidth()) {
                StoreBadge(
                    icon = HavnIconKey.Apple,
                    eyebrow = "Download on the",
                    label = "App Store",
                    url = AppStoreUrl,
                    modifier = Modifier.fillMaxWidth()
                )
                StoreBadge(
                    icon = HavnIconKey.Google,
                    eyebrow = "Get it on",
                    label = "Google Play",
                    url = GooglePlayUrl,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        } else {
            Row(horizontalArrangement = arrangement, modifier = Modifier.fillMaxWidth()) {
                StoreBadge(
                    icon = HavnIconKey.Apple,
                    eyebrow = "Download on the",
                    label = "App Store",
                    url = AppStoreUrl,
                    modifier = Modifier.weight(1f)
                )
                StoreBadge(
                    icon = HavnIconKey.Google,
                    eyebrow = "Get it on",
                    label = "Google Play",
                    url = GooglePlayUrl,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
private fun StoreBadge(
    icon: HavnIconKey,
    eyebrow: String,
    label: String,
    url: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .height(54.dp)
            .clip(MaterialTheme.shapes.medium)
            .clickable { HavnExternalActions.openUrl(url) },
        shape = MaterialTheme.shapes.medium,
        color = theme.surface.copy(alpha = 0.82f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.9f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnIcon(icon, contentDescription = null, tint = theme.primaryText, modifier = Modifier.size(24.dp))
            Column(verticalArrangement = Arrangement.spacedBy(1.dp)) {
                Text(eyebrow, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
                Text(
                    label,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
private fun VerifyCodeScreen(
    uiState: AuthUiState,
    onOtpCodeChanged: (String) -> Unit,
    onVerifyEmailOtp: () -> Unit,
    onResend: () -> Unit,
    onBack: () -> Unit
) {
    AuthFormFrame(
        subtitle = "Enter the code from your email, or open the magic link on this device.",
        compact = true
    ) {
        HavnTextField(
            value = uiState.otpCode,
            onValueChange = onOtpCodeChanged,
            label = "Email code",
            isError = uiState.otpCodeError != null,
            errorText = uiState.otpCodeError,
            leadingIcon = HavnIconKey.Check,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth()
        )
        HavnButton(
            text = "Verify code",
            onClick = onVerifyEmailOtp,
            enabled = !uiState.isSubmitting,
            loading = uiState.isSubmitting,
            leadingIcon = HavnIconKey.Check,
            modifier = Modifier.fillMaxWidth()
        )
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnButton(
                text = "Back",
                onClick = onBack,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Ghost,
                leadingIcon = HavnIconKey.Back,
                modifier = Modifier.weight(1f)
            )
            HavnButton(
                text = "Resend",
                onClick = onResend,
                enabled = !uiState.isSubmitting,
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Mail,
                modifier = Modifier.weight(1f)
            )
        }
        AuthMessages(uiState)
    }
}

@Composable
private fun AuthFormFrame(
    subtitle: String,
    showLogo: Boolean = false,
    centerSubtitle: Boolean = false,
    compact: Boolean = false,
    content: @Composable () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val horizontalPadding = if (compact) 20.dp else 24.dp
    val verticalPadding = if (compact) 22.dp else 34.dp
    val logoSize = if (compact) 76.dp else 112.dp
    val cardPadding = if (compact) 14.dp else 18.dp
    val cardSpacing = if (compact) 10.dp else 14.dp
    val contentModifier = Modifier
        .widthIn(max = 420.dp)
        .fillMaxWidth()
    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(Brush.verticalGradient(listOf(theme.background, theme.surface, theme.background)))
            .verticalScroll(rememberScrollState())
            .padding(horizontal = horizontalPadding, vertical = verticalPadding),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (showLogo) {
            Image(
                painter = painterResource(Res.drawable.havn_auth_logo),
                contentDescription = "HAVN",
                modifier = Modifier.size(logoSize)
            )
            Spacer(Modifier.height(8.dp))
            Text(
                "HAVN Real Estate",
                color = theme.primaryText,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center,
                modifier = contentModifier
            )
            Spacer(Modifier.height(10.dp))
        }
        Text(
            subtitle,
            color = theme.secondaryText,
            style = MaterialTheme.typography.bodyLarge,
            textAlign = if (centerSubtitle) TextAlign.Center else TextAlign.Start,
            modifier = contentModifier
        )
        Spacer(Modifier.height(18.dp))
        Surface(
            color = theme.card,
            shape = MaterialTheme.shapes.large,
            border = BorderStroke(1.dp, theme.border),
            modifier = contentModifier
        ) {
            Column(
                modifier = Modifier.padding(cardPadding),
                verticalArrangement = Arrangement.spacedBy(cardSpacing)
            ) {
                content()
            }
        }
    }
}

@Composable
private fun AuthMessages(uiState: AuthUiState) {
    uiState.statusMessage?.let { message ->
        HelperText(message)
    }
    uiState.submitError?.let { error ->
        Text(error, color = LocalHavnThemeTokens.current.danger, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun HelperText(text: String) {
    Text(
        text,
        color = LocalHavnThemeTokens.current.mutedText,
        style = MaterialTheme.typography.bodySmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth()
    )
}
